/**
 * [ui1006, paczka KIT] REJESTR OBSZARÓW — „Zarządzanie Office 365” (`/admin/office365/…`).
 *
 * Jedno źródło nawigacji strony: 5 grup, 21 obszarów (2 + 4 + 3 + 6 + 6) — rozdz. 6 projektu ui1006 — zamiast dziesięciu
 * równych zakładek w przewijanym pasku. Grupa nazywa się „Wykrywanie” (nie „Reguły”), bo etykieta grupy nie może
 * powtarzać etykiety swojego obszaru (zapadka N-17).
 *
 * Parametry: `?spolka=` jest wszędzie FILTREM (tak stoi w linkach powiadomień zapisanych w bazie); spółka-obiekt to
 * `?karta=ID`; konto i reguła jako FILTR to `?filtrKonto=` i `?filtrRegula=`, bo `?konto=` i `?regula=` otwierają obiekty.
 *
 * `aktywna: false` — paczka KIT tylko zakłada rejestr; stronę przełącza paczka O365 (po rozbudowie Office 365 i po paczce
 * INT). Trzy miejsca oznaczone `wkrotce` (Reakcje, Mapa, Analityka) to funkcje, które w chwili pisania rejestru dopiero
 * nadchodzą z gałęzi rozbudowy — paczka O365 zdejmuje znacznik, gdy funkcja jest na main.
 */
import {
    FiList, FiSun, FiAlertOctagon, FiBell, FiUsers, FiZap, FiBookOpen, FiSlash, FiClipboard, FiShield, FiSearch,
    FiMap, FiBarChart2, FiArchive, FiFileText, FiPlayCircle, FiKey, FiServer, FiClock, FiTrash2, FiHelpCircle,
} from "react-icons/fi";
import { GRUPA_STARTU, GRUPA_PRACY, GRUPA_USTAWIEN, grupaStartu, grupaPracy, grupaUstawien, obszar, obszarStartu } from "./ksztalt";

const GRUPA_WYKRYWANIA = "wykrywanie";
const GRUPA_DOWODOW = "stan-i-dowody";

/**
 * Cel ZASTĘPCZY dawnej zakładki „Zarządzanie” na czas, gdy miejsce „Reakcje” jest dopiero zapowiedziane (`wkrotce`):
 * „Dziennik modułu” — istnieje, stoi za tym samym kluczem (`Office365Manage`) i jest żywą częścią dawnej zakładki.
 * [poprawka po przeglądzie 2026-10-06] BYŁO: stary link `?zakladka=zarzadzanie` zostawał na starcie strony z martwym
 * zapytaniem, choć dawna zakładka ma dziś żywą treść (przypisanie kont, dziennik). Parametry obiektów „Reakcji”
 * (`wniosek`, `akcja`) nie idą do celu zastępczego — w dzienniku nic by nie znaczyły.
 * Gdy paczka O365 zdejmie `wkrotce` z „Reakcji”, cel zastępczy przestaje działać sam (wygrywa cel właściwy).
 */
const REAKCJE = { obszar: "reakcje", zastepczo: { obszar: "dziennik", bez: ["wniosek", "akcja"] } };

/** Dawna zakładka (`?zakladka=`, także aliasy z linków powiadomień) → nowy obszar (+ zapytanie). Tabela 6.7 projektu. */
const ZAKLADKA_NA_OBSZAR = {
    przeglad: { obszar: "start" },
    digest: { obszar: "przeglad-dzienny" },
    digesty: { obszar: "przeglad-dzienny" },
    przeglad_dzienny: { obszar: "przeglad-dzienny" },
    incydenty: { obszar: "incydenty" },
    incydent: { obszar: "incydenty" },
    alerty: { obszar: "alerty" },
    alert: { obszar: "alerty" },
    konta: { obszar: "konta" },
    konto: { obszar: "konta" },
    reguly: { obszar: "reguly" },
    regula: { obszar: "reguly" },
    wyjatki: { obszar: "wylaczenia" },
    wyjatek: { obszar: "wylaczenia" },
    listy: { obszar: "listy" },
    lista: { obszar: "listy" },
    polityki: { obszar: "zabezpieczenia-spolek" },
    polityka: { obszar: "zabezpieczenia-spolek" },
    zarzadzanie: REAKCJE,
    akcje: REAKCJE,
    mapowanie: { obszar: "konta", dopisz: { widok: "do-przypisania" } },
    zdrowie: { obszar: "ustawienia-zgody", zAlertem: { obszar: "alerty", dopisz: { rodzaj: "zdrowie" } } },
    "zdrowie-i-eksport": { obszar: "ustawienia-zgody", zAlertem: { obszar: "alerty", dopisz: { rodzaj: "zdrowie" } } },
    zdrowie_i_eksport: { obszar: "ustawienia-zgody", zAlertem: { obszar: "alerty", dopisz: { rodzaj: "zdrowie" } } },
    eksport: { obszar: "eksporty" },
    eksporty: { obszar: "eksporty" },
};

/** Obszary-listy, w których filtr „Status” istnieje tylko pod żetonem „Wszystkie” — stary `?status=` włącza ten żeton. */
const OBSZARY_ZE_STATUSEM = ["incydenty", "alerty"];

/**
 * STARE LINKI (tabela 6.7 projektu) — czysta funkcja: działa tylko na adresie bazowym strony.
 * Linki są zapisane w bazie (powiadomienia, e-maile) i klikane także wtedy, gdy człowiek JUŻ jest na tej stronie —
 * dlatego powłoka woła przekierowanie przy każdej zmianie adresu, nie tylko przy montażu.
 */
const stareAdresy = ({ reszta, parametry }) => {
    if (reszta) return null;
    const surowa = String(parametry.get("zakladka") || "").trim().toLowerCase();
    let cel = ZAKLADKA_NA_OBSZAR[surowa] || null;
    if (cel && cel.zAlertem && parametry.get("alert")) cel = cel.zAlertem;
    if (!cel) {
        // Bez zakładki (albo z nieznaną): obiekt w zapytaniu sam mówi, dokąd prowadzi link.
        if (parametry.get("incydent")) cel = { obszar: "incydenty" };
        else if (parametry.get("alert")) cel = { obszar: "alerty" };
        else if (parametry.get("konto")) cel = { obszar: "konta" };
        else if (parametry.get("wniosek") || parametry.get("akcja")) cel = REAKCJE;
        else if (surowa) cel = { obszar: "start" };
        else return null;
    }
    const nowe = new URLSearchParams(parametry);
    nowe.delete("zakladka");
    Object.entries(cel.dopisz || {}).forEach(([nazwa, wartosc]) => { if (!nowe.has(nazwa)) nowe.set(nazwa, wartosc); });
    if (OBSZARY_ZE_STATUSEM.includes(cel.obszar) && nowe.get("status") && !nowe.has("widok")) nowe.set("widok", "wszystkie");
    const opis = { obszar: cel.obszar, zapytanie: nowe };
    if (cel.zastepczo) {
        // Cel na czas zapowiedzi miejsca właściwego — wybiera go `celStaregoAdresu`, gdy miejsce właściwe nie ma jeszcze adresu.
        const zastepcze = new URLSearchParams(nowe);
        (cel.zastepczo.bez || []).forEach((nazwa) => zastepcze.delete(nazwa));
        opis.zastepczo = { obszar: cel.zastepczo.obszar, zapytanie: zastepcze };
    }
    return opis;
};

const office365 = {
    strona: "office365",
    tytul: "Zarządzanie Office 365",
    zdanie: "Pilnuje bezpieczeństwa chmury Microsoft 365 każdej spółki.",
    baza: { pl: "/admin/office365", en: "/admin/office365" },
    kluczStrony: "Office365View",
    aktywna: false,

    grupy: [
        grupaStartu(),
        grupaPracy(),
        { id: GRUPA_WYKRYWANIA, etykieta: "Wykrywanie" },
        { id: GRUPA_DOWODOW, etykieta: "Stan i dowody" },
        grupaUstawien(),
    ],

    obszary: [
        obszarStartu({
            kluczTrasy: "adminModule",
            zdanie: "Pilnuje bezpieczeństwa chmury Microsoft 365 każdej spółki.",
            ikona: FiList,
            filtry: ["spolka", "moje"],
            slowa: ["przegląd", "do załatwienia", "co wymaga uwagi", "start"],
        }),
        obszar({
            id: "przeglad-dzienny", grupa: GRUPA_STARTU, segment: { pl: "przeglad-dzienny", en: "daily-review" }, kluczTrasy: "adminO365Przeglad",
            etykieta: "Przegląd dzienny", zdanie: "Tu czytasz dzienne i tygodniowe podsumowanie alertów i incydentów.",
            ikona: FiSun, wzor: "lista dat + treść",
            filtry: ["dzien", "spolka"],
            slowa: ["przegląd dzienny", "przegląd tygodniowy", "podsumowanie dnia"],
        }),

        // ── Praca ──────────────────────────────────────────────────────────────────────────────
        obszar({
            id: "incydenty", grupa: GRUPA_PRACY, segment: { pl: "incydenty", en: "incidents" }, kluczTrasy: "adminO365Incydenty",
            etykieta: "Incydenty", zdanie: "Tu obsługujesz sprawy bezpieczeństwa.",
            ikona: FiAlertOctagon, licznik: true,
            widoki: [
                { id: "otwarte", etykieta: "Otwarte" },
                { id: "po-terminie", etykieta: "Po terminie", licznik: true },
                { id: "wszystkie", etykieta: "Wszystkie" },
            ],
            filtry: ["spolka", "poziom", "moje", "status"],
            obiekty: [{
                parametr: "incydent", rodzaj: "karta", nazwa: "incydent", dopelniacz: "incydentu",
                czesci: ["podsumowanie", "alerty", "os-czasu", "reakcje"],
            }],
            glowa: { analityka: true },
            pasek: { szukajka: "Szukaj incydentu…", wiecej: ["Pobierz (CSV)"] },
            slowa: ["incydent", "otwarte incydenty", "termin obsługi", "przypisany do mnie"],
        }),
        obszar({
            id: "alerty", grupa: GRUPA_PRACY, segment: { pl: "alerty", en: "alerts" }, kluczTrasy: "adminO365Alerty",
            etykieta: "Alerty", zdanie: "Tu przeglądasz pojedyncze sygnały reguł.",
            ikona: FiBell, wzor: "S2 + S7", licznik: true,
            widoki: [
                { id: "nowe", etykieta: "Nowe", licznik: true },
                { id: "przejrzane", etykieta: "Przejrzane" },
                { id: "wszystkie", etykieta: "Wszystkie" },
            ],
            filtry: ["spolka", "poziom", "filtrRegula", "rodzaj", "status"],
            obiekty: [{ parametr: "alert", rodzaj: "szuflada", nazwa: "alert", dopelniacz: "alertu" }],
            glowa: { analityka: true, zdanieStanu: "Zaznaczono 3 · Odznacz" },
            pasek: { szukajka: "Szukaj alertu…", akcjaPoZaznaczeniu: "Utwórz incydent", wiecej: ["Pobierz (CSV)"] },
            slowa: ["alert", "sygnał", "przejrzany", "alert zdrowia", "tryb nauki"],
        }),
        obszar({
            id: "konta", grupa: GRUPA_PRACY, segment: { pl: "konta", en: "accounts" }, kluczTrasy: "adminO365Konta",
            etykieta: "Konta", zdanie: "Tu widzisz stan kont Microsoft 365.",
            ikona: FiUsers, licznik: true,
            widoki: [
                { id: "wszystkie", etykieta: "Wszystkie" },
                { id: "do-przypisania", etykieta: "Do przypisania", licznik: true },
                { id: "inna-spolka", etykieta: "Inna spółka", licznik: true },
            ],
            filtry: ["zestaw", "spolka"],
            obiekty: [{
                parametr: "konto", rodzaj: "karta", nazwa: "konto", dopelniacz: "konta",
                czesci: ["podsumowanie", "dostep", "historia", "aktywnosc", "reguly", "grozne"],
            }],
            glowa: { analityka: true, zdanieStanu: "stan z 06.10 06:00" },
            pasek: { szukajka: "Szukaj konta…", wiecej: ["Pobierz stan kont z Microsoft…", "Pobierz (CSV)"] },
            slowa: ["konto", "MFA", "role", "licencje", "mapowanie", "przypisanie do pracownika", "poziom ochrony"],
        }),
        obszar({
            id: "reakcje", grupa: GRUPA_PRACY, segment: { pl: "reakcje", en: "responses" }, kluczTrasy: "adminO365Reakcje",
            etykieta: "Reakcje", zdanie: "Tu zatwierdzasz i śledzisz działania na kontach.",
            ikona: FiZap, klucz: "Office365Manage", wkrotce: true, licznik: true,
            widoki: [
                { id: "do-zatwierdzenia", etykieta: "Do zatwierdzenia", licznik: true },
                { id: "historia", etykieta: "Historia" },
            ],
            obiekty: [
                { parametr: "wniosek", rodzaj: "karta", nazwa: "wniosek o reakcję", dopelniacz: "wniosku o reakcję" },
                { parametr: "akcja", rodzaj: "szuflada", nazwa: "reakcja", dopelniacz: "reakcji" },
            ],
            // Karta „Analityka” dawnej zakładki „Zarządzanie” stoi w „Reakcjach” i w „Dzienniku modułu” (tabela 6.10 projektu).
            glowa: { analityka: true },
            slowa: ["zarządzanie", "akcje", "zablokuj konto", "Reaktor", "wniosek o reakcję"],
        }),

        // ── Wykrywanie ─────────────────────────────────────────────────────────────────────────
        obszar({
            id: "reguly", grupa: GRUPA_WYKRYWANIA, segment: { pl: "reguly", en: "rules" }, kluczTrasy: "adminO365Reguly",
            etykieta: "Reguły", zdanie: "Tu widzisz, co wykrywamy i jak czule.",
            ikona: FiBookOpen, wzor: "S2 (grupy)",
            filtry: ["stanReguly", "spolka"],
            obiekty: [{
                parametr: "regula", rodzaj: "karta", nazwa: "reguła", dopelniacz: "reguły",
                czesci: ["opis", "spolki", "konta", "progi", "historia"],
            }],
            glowa: { analityka: true },
            pasek: { szukajka: "Szukaj reguły…" },
            slowa: ["reguła", "katalog reguł", "progi", "poziom", "włączona w spółce"],
        }),
        obszar({
            id: "wylaczenia", grupa: GRUPA_WYKRYWANIA, segment: { pl: "wylaczenia", en: "exclusions" }, kluczTrasy: "adminO365Wylaczenia",
            etykieta: "Wyłączenia", zdanie: "Tu widzisz, komu i do kiedy wyciszyliśmy regułę.",
            ikona: FiSlash, wzor: "S2 + S7", licznik: true,
            widoki: [
                { id: "aktywne", etykieta: "Aktywne" },
                { id: "wygasajace", etykieta: "Wygasające", licznik: true },
                { id: "wygasle", etykieta: "Wygasłe" },
                { id: "przywrocone", etykieta: "Przywrócone" },
            ],
            obiekty: [{ parametr: "wylaczenie", rodzaj: "szuflada", nazwa: "wyłączenie reguły", dopelniacz: "wyłączenia reguły" }],
            kreatory: [{ nazwa: "wylaczenie", tytul: "Dodaj wyłączenie", kroki: 4 }],
            glowa: { analityka: true },
            pasek: { szukajka: "Szukaj wyłączenia…", akcjaGlowna: "Dodaj wyłączenie", wiecej: ["Pobierz (CSV)"] },
            slowa: ["wyjątki i wyłączenia", "wyjątek", "wyciszenie", "nie alarmuj", "przywróć regułę"],
        }),
        obszar({
            id: "listy", grupa: GRUPA_WYKRYWANIA, segment: { pl: "listy", en: "lists" }, kluczTrasy: "adminO365Listy",
            etykieta: "Listy", zdanie: "Tu prowadzisz listy, z których korzystają reguły.",
            ikona: FiClipboard,
            obiekty: [{ parametr: "lista", rodzaj: "karta", nazwa: "lista", dopelniacz: "listy", czesci: ["wpisy", "automatyczne"] }],
            glowa: { analityka: true },
            slowa: ["listy obserwacyjne", "zaufane kraje", "adresy firmowe", "wpis listy"],
        }),

        // ── Stan i dowody ──────────────────────────────────────────────────────────────────────
        obszar({
            id: "zabezpieczenia-spolek", grupa: GRUPA_DOWODOW, segment: { pl: "zabezpieczenia-spolek", en: "tenant-security" }, kluczTrasy: "adminO365Zabezpieczenia",
            etykieta: "Zabezpieczenia spółek", zdanie: "Tu sprawdzasz ustawienia zabezpieczeń Microsoft 365 każdej spółki.",
            ikona: FiShield,
            obiekty: [{
                parametr: "karta", rodzaj: "karta", nazwa: "zabezpieczenia spółki", dopelniacz: "zabezpieczeń spółki",
                czesci: ["odchylenia", "ustawienia", "synchronizacja-ad", "domeny", "poczta-i-aplikacje", "pozostale"],
            }],
            glowa: { analityka: true },
            slowa: ["polityki", "ustawienia zabezpieczeń", "co odbiega od wzorca", "domeny", "rekordy DNS"],
        }),
        obszar({
            id: "zdarzenia", grupa: GRUPA_DOWODOW, segment: { pl: "zdarzenia", en: "events" }, kluczTrasy: "adminO365Zdarzenia",
            etykieta: "Zdarzenia", zdanie: "Tu szukasz zdarzenia potrzebnego do śledztwa.",
            ikona: FiSearch, wzor: "wyszukiwarka + S7",
            filtry: ["filtrKonto", "ip", "operacja", "od", "do", "kraj", "siec", "usluga", "wynik"],
            obiekty: [{ parametr: "zdarzenie", rodzaj: "szuflada", nazwa: "zdarzenie", dopelniacz: "zdarzenia" }],
            pasek: { akcjaGlowna: "Szukaj", wiecej: ["Pobierz wyniki (CSV)", "Pobierz wyniki (JSONL)"] },
            slowa: ["przeglądarka zdarzeń", "logowania", "adres IP", "operacja", "surowy zapis"],
        }),
        obszar({
            id: "mapa", grupa: GRUPA_DOWODOW, segment: { pl: "mapa", en: "map" }, kluczTrasy: "adminO365Mapa",
            etykieta: "Mapa", zdanie: "Tu widzisz, skąd przychodzą próby logowania.",
            ikona: FiMap, wzor: "mapa + lista krajów", wkrotce: true,
            filtry: ["okres", "spolka"],
            slowa: ["mapa", "kraje", "skąd logowania"],
        }),
        obszar({
            id: "analityka", grupa: GRUPA_DOWODOW, segment: { pl: "analityka", en: "analytics" }, kluczTrasy: "adminO365Analityka",
            etykieta: "Analityka", zdanie: "Tu oglądasz wszystkie wykresy jednego tematu naraz.",
            ikona: FiBarChart2, wzor: "tematy analityki", wkrotce: true,
            filtry: ["temat", "okres", "spolka"],
            slowa: ["analityka", "wykresy", "trend", "statystyki"],
        }),
        obszar({
            id: "eksporty", grupa: GRUPA_DOWODOW, segment: { pl: "eksporty", en: "exports" }, kluczTrasy: "adminO365Eksporty",
            etykieta: "Eksporty dzienne", zdanie: "Tu sprawdzasz, czy dzienne pliki dowodowe są kompletne.",
            ikona: FiArchive,
            filtry: ["spolka", "od", "do"],
            glowa: { analityka: true },
            pasek: { akcjaGlowna: "Sprawdź ciągłość…" },
            slowa: ["zdrowie i eksport", "eksport", "pliki dowodowe", "ciągłość", "kod kontrolny"],
        }),
        obszar({
            id: "dziennik", grupa: GRUPA_DOWODOW, segment: { pl: "dziennik", en: "audit-log" }, kluczTrasy: "adminO365Dziennik",
            etykieta: "Dziennik modułu", zdanie: "Tu widzisz, kto co zrobił w module.",
            ikona: FiFileText, klucz: "Office365Manage", wzor: "S2 + S7",
            filtry: ["rodzaj"],
            obiekty: [{ parametr: "wpis", rodzaj: "szuflada", nazwa: "wpis dziennika", dopelniacz: "wpisu dziennika" }],
            glowa: { analityka: true },
            pasek: { wiecej: ["Pobierz (CSV)"] },
            slowa: ["dziennik", "zarządzanie", "kto co zrobił", "historia zmian"],
        }),

        // ── Ustawienia ─────────────────────────────────────────────────────────────────────────
        obszar({
            id: "ustawienia-uruchomienie", grupa: GRUPA_USTAWIEN, segment: { pl: "uruchomienie", en: "setup" },
            kluczTrasy: "adminO365UstUruchomienie",
            etykieta: "Uruchomienie", zdanie: "Tu widzisz, co zostało do zrobienia, żeby monitorowanie zaczęło działać.",
            ikona: FiPlayCircle, wzor: "S4",
            slowa: ["co zrobić, żeby zaczęło działać", "kroki uruchomienia", "monitorowanie", "odbiorcy powiadomień"],
        }),
        obszar({
            id: "ustawienia-zgody", grupa: GRUPA_USTAWIEN, segment: { pl: "zgody", en: "consents" },
            kluczTrasy: "adminO365UstZgody",
            etykieta: "Zgody aplikacji", zdanie: "Tu sprawdzasz, czy aplikacja ma w każdej spółce zgody do odczytu bezpieczeństwa.",
            ikona: FiKey, wzor: "S2 (odczyt)",
            filtry: ["spolka"],
            glowa: { analityka: true },
            slowa: ["zdrowie i eksport", "zgody", "uprawnienia aplikacji", "Czytelnik", "sprawdź ponownie"],
        }),
        obszar({
            id: "ustawienia-zrodla", grupa: GRUPA_USTAWIEN, segment: { pl: "zrodla", en: "sources" },
            kluczTrasy: "adminO365UstZrodla",
            etykieta: "Źródła danych i serwer", zdanie: "Tu sprawdzasz, czy dane z Microsoft 365 napływają i jak działa moduł na serwerze.",
            ikona: FiServer, wzor: "S2 + karta",
            filtry: ["spolka"],
            obiekty: [{ parametr: "zrodlo", rodzaj: "szuflada", nazwa: "źródło danych", dopelniacz: "źródła danych" }],
            slowa: ["zdrowie i eksport", "zdrowie zbierania", "opóźnienie", "moduł na serwerze", "geolokalizacja"],
        }),
        obszar({
            id: "ustawienia-terminy", grupa: GRUPA_USTAWIEN, segment: { pl: "terminy-i-przeglad", en: "response-times-and-review" },
            kluczTrasy: "adminO365UstTerminy",
            etykieta: "Terminy i przegląd dzienny", zdanie: "Tu ustawiasz terminy obsługi incydentów i godzinę przeglądu dziennego.",
            ikona: FiClock, wzor: "S3",
            pasek: { akcjaGlowna: "Zapisz zmiany (2)" },
            slowa: ["terminy obsługi", "godzina przeglądu", "tryb nauki", "ustawienia modułu"],
        }),
        obszar({
            id: "ustawienia-przechowywanie", grupa: GRUPA_USTAWIEN, segment: { pl: "przechowywanie", en: "retention" },
            kluczTrasy: "adminO365UstPrzechowywanie",
            etykieta: "Przechowywanie danych", zdanie: "Tu zmieniasz, jak długo trzymamy zdarzenia i pliki dowodowe.",
            ikona: FiTrash2, wzor: "sama Strefa groźna",
            slowa: ["retencja", "czas przechowywania", "katalogi na serwerze", "kasowanie danych"],
        }),
        obszar({
            id: "ustawienia-jak-dziala", grupa: GRUPA_USTAWIEN, segment: { pl: "jak-to-dziala", en: "how-it-works" },
            kluczTrasy: "adminO365UstJakDziala",
            etykieta: "Jak to działa", zdanie: "Tu czytasz, jak działa moduł, kto ma dostęp i kto co robi.",
            ikona: FiHelpCircle, wzor: "strona pomocy",
            slowa: ["przewodnik", "jak to działa", "dwa tory uprawnień", "dwa miejsca — kto co robi"],
        }),
    ],

    trasyObiektow: [],

    /** Źródła listy „Do załatwienia” wypełnia paczka O365; plan: sześć lekkich odczytów głównych (6.2 projektu). */
    zrodlaStartu: [],
    sufitOdczytowStartu: 7,
    zrodlaStartuPlan: [
        { id: "przeglad", odczyt: "GET przeglad", klucz: "Office365View" },
        { id: "incydenty", odczyt: "GET incydenty (otwarte, pierwsza strona)", klucz: "Office365View" },
        { id: "zdrowie", odczyt: "GET zdrowie", klucz: "Office365View" },
        { id: "zgody", odczyt: "GET zgody (zapisany stan, bez odświeżania)", klucz: "Office365View" },
        { id: "wylaczenia", odczyt: "GET wyjatki (wygasające)", klucz: "Office365View" },
        { id: "wnioski", odczyt: "GET akcje/wnioski", klucz: "Office365Manage" },
    ],

    stareAdresy,
    stareSlugi: {},

    slownikPojec: [
        { slowo: "incydent", definicja: "Sprawa bezpieczeństwa do obsłużenia, złożona z jednego albo kilku powiązanych alertów." },
        { slowo: "alert", definicja: "Pojedynczy sygnał reguły, że na koncie albo w ustawieniach spółki stało się coś podejrzanego." },
        { slowo: "poziom", definicja: "Waga alertu albo incydentu: krytyczny, wysoki, do przeglądu dziennego albo do przeglądu tygodniowego." },
        { slowo: "termin obsługi", definicja: "Czas, w którym incydent danego poziomu powinien zostać podjęty i zamknięty." },
        { slowo: "reguła", definicja: "Opis zachowania, które system uznaje za podejrzane i dla którego tworzy alert." },
        { slowo: "wyłączenie", definicja: "Decyzja, że reguła nie alarmuje dla wskazanego konta, adresu albo domeny do podanego dnia." },
        { slowo: "lista", definicja: "Zbiór wartości, na przykład krajów albo adresów, z którego korzystają reguły." },
        { slowo: "poziom ochrony konta", definicja: "Stopień czułości reguł dla konta, wyższy dla zarządu, finansów i kont awaryjnych." },
        { slowo: "tryb nauki", definicja: "Okres, w którym system poznaje zwyczaje logowania spółki, a logowania z nowych krajów nie tworzą incydentów." },
        { slowo: "punkty ryzyka", definicja: "Liczba, która rośnie z każdym alertem konta i pomaga wskazać konta wymagające uwagi." },
        { slowo: "reakcja", definicja: "Działanie na koncie w Microsoft 365 wykonane z aplikacji, na przykład zablokowanie konta." },
        { slowo: "wniosek o reakcję", definicja: "Prośba o wykonanie reakcji, którą musi zatwierdzić druga osoba." },
        { slowo: "eksport dzienny", definicja: "Plik z zapisem zdarzeń jednego dnia, odkładany jako dowód." },
        { slowo: "ciągłość eksportu", definicja: "Sprawdzenie kodami kontrolnymi, czy żadnego pliku dziennego nie brakuje i żaden nie został zmieniony." },
        { slowo: "Czytelnik", definicja: "Rejestracja w Microsoft 365, która tylko czyta logowania i ustawienia potrzebne regułom bezpieczeństwa." },
        { slowo: "Reaktor", definicja: "Opcjonalna rejestracja w Microsoft 365, która pozwala aplikacji blokować konta i wykonywać inne reakcje." },
        { slowo: "data poinformowania pracowników", definicja: "Dzień, w którym spółka powiadomiła pracowników o monitorowaniu, wymagany przed jego włączeniem." },
    ],

    gdzieCoBylo: [
        { bylo: "Przegląd", jest: "Do załatwienia — lista rzeczy do zrobienia; podsumowania w „Przegląd dzienny”", obszar: "start" },
        { bylo: "Incydenty", jest: "Praca → Incydenty", obszar: "incydenty" },
        { bylo: "Alerty", jest: "Praca → Alerty", obszar: "alerty" },
        { bylo: "Konta", jest: "Praca → Konta", obszar: "konta" },
        { bylo: "Reguły", jest: "Wykrywanie → Reguły", obszar: "reguly" },
        { bylo: "Wyjątki i wyłączenia", jest: "Wykrywanie → Wyłączenia", obszar: "wylaczenia" },
        { bylo: "Listy obserwacyjne", jest: "Wykrywanie → Listy", obszar: "listy" },
        { bylo: "Polityki", jest: "Stan i dowody → Zabezpieczenia spółek", obszar: "zabezpieczenia-spolek" },
        { bylo: "Zarządzanie: akcje na kontach", jest: "Praca → Reakcje", obszar: "reakcje" },
        { bylo: "Zarządzanie: przypisanie kont do pracowników", jest: "Praca → Konta, widok „Do przypisania”", obszar: "konta" },
        { bylo: "Zarządzanie: dziennik", jest: "Stan i dowody → Dziennik modułu", obszar: "dziennik" },
        { bylo: "Zdrowie i eksport: zgody", jest: "Ustawienia → Zgody aplikacji", obszar: "ustawienia-zgody" },
        { bylo: "Zdrowie i eksport: źródła i serwer", jest: "Ustawienia → Źródła danych i serwer", obszar: "ustawienia-zrodla" },
        { bylo: "Zdrowie i eksport: eksport dzienny", jest: "Stan i dowody → Eksporty dzienne", obszar: "eksporty" },
        { bylo: "Zdrowie i eksport: ustawienia modułu", jest: "Ustawienia → Terminy i przegląd dzienny oraz Przechowywanie danych", obszar: "ustawienia-terminy" },
        { bylo: "Przewodnik „Jak to działa” nad zakładkami", jest: "Ustawienia → Jak to działa", obszar: "ustawienia-jak-dziala" },
        { bylo: "Przewodnik: „Co zrobić, żeby zaczęło działać”", jest: "Ustawienia → Uruchomienie", obszar: "ustawienia-uruchomienie" },
    ],

    stareNazwyMiejsc: [
        [/Zdrowie i eksport/g, "Ustawienia → Zgody aplikacji"],
        [/Wyjątki i wyłączenia/g, "Wykrywanie → Wyłączenia"],
        [/Listy obserwacyjne/g, "Wykrywanie → Listy"],
        [/Integracje\s*(?:→|->)\s*Reguły incydentów/g, "Integracje → Bezpieczeństwo Office 365"],
    ],
};

export default office365;
